"use client"

import { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { MoreHorizontal, Trash2, Loader2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { deleteCampaign, getCampaigns, type Campaign } from "@/lib/marketing-api"
import { useClientContext } from "@/lib/client-context"

const statusConfig: Record<string, { label: string; className: string }> = {
  active: { label: "Live", className: "bg-success/20 text-success border-success/30" },
  paused: { label: "Paused", className: "bg-warning/20 text-warning border-warning/30" },
  draft: { label: "Draft", className: "bg-muted text-muted-foreground border-muted" },
  completed: { label: "Completed", className: "bg-secondary text-secondary-foreground border-secondary" },
  archived: { label: "Archived", className: "bg-secondary text-secondary-foreground border-secondary" },
}

function fmtNum(v = 0) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`
  return String(v)
}

function fmtCurrency(v = 0) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v)
}

interface CampaignTableProps {
  variant?: "campaign" | "sp"
}

export function CampaignTable({ variant = "campaign" }: CampaignTableProps) {
  const labels =
    variant === "sp"
      ? {
          primary: "Service Line",
          platform: "Channel",
          leads: "Requests",
          cpl: "Cost / Request",
          pause: "Pause Queue",
          resume: "Resume Queue",
          duplicate: "Duplicate Template",
          delete: "Delete Template",
        }
      : {
          primary: "Campaign",
          platform: "Platform",
          leads: "Leads",
          cpl: "CPL",
          pause: "Pause Campaign",
          resume: "Resume Campaign",
          duplicate: "Duplicate",
          delete: "Delete",
        }

  const { activeClient } = useClientContext()
  const [items, setItems] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true)
    try {
      const res = await getCampaigns({ clientId: activeClient?._id, limit: 100 })
      setItems(res.data?.campaigns || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient?._id])

  const rows = useMemo(() => items, [items])

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>

  if (rows.length === 0) return <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">No campaigns found.</div>

  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="text-muted-foreground">{labels.primary}</TableHead>
            <TableHead className="text-muted-foreground">Status</TableHead>
            <TableHead className="text-muted-foreground">Type</TableHead>
            <TableHead className="text-muted-foreground hidden sm:table-cell">{labels.platform}</TableHead>
            <TableHead className="text-muted-foreground text-right">Budget</TableHead>
            <TableHead className="text-muted-foreground text-right">Spent</TableHead>
            <TableHead className="text-muted-foreground text-right hidden lg:table-cell">Impressions</TableHead>
            <TableHead className="text-muted-foreground text-right hidden md:table-cell">Clicks</TableHead>
            <TableHead className="text-muted-foreground text-right">{labels.leads}</TableHead>
            <TableHead className="text-muted-foreground w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((campaign) => {
            const st = statusConfig[campaign.status] || statusConfig.draft
            return (
              <TableRow key={campaign._id} className="border-border hover:bg-secondary/50">
                <TableCell className="font-medium text-card-foreground">
                  <Link href={`/campaigns/${campaign._id}`} className="hover:underline">{campaign.name}</Link>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={cn("font-medium", st.className)}>{st.label}</Badge>
                </TableCell>
                <TableCell className="capitalize">{(campaign.type || "paid_ads").replaceAll("_", " ")}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {(campaign.platforms || []).length > 0 ? campaign.platforms.map((p) => (
                      <Badge key={p} variant="outline" className="text-xs capitalize">{p}</Badge>
                    )) : <span className="text-muted-foreground text-xs">—</span>}
                  </div>
                </TableCell>
                <TableCell className="text-right text-card-foreground">{fmtCurrency(campaign.budget || 0)}</TableCell>
                <TableCell className="text-right text-card-foreground">{fmtCurrency(campaign.spent || 0)}</TableCell>
                <TableCell className="text-right text-card-foreground hidden lg:table-cell">{fmtNum(campaign.impressions || 0)}</TableCell>
                <TableCell className="text-right text-card-foreground hidden md:table-cell">{fmtNum(campaign.clicks || 0)}</TableCell>
                <TableCell className="text-right text-card-foreground">{fmtNum(campaign.leads || 0)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link href={`/campaigns/${campaign._id}`}>View Details</Link>
                      </DropdownMenuItem>
                      {campaign.type === 'content_creation' && (
                        <DropdownMenuItem asChild>
                          <Link href={`/analytics/${campaign._id}`}>View Analytics</Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={async () => { await deleteCampaign(campaign._id); await load() }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />{labels.delete}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
